import { call, put, select, takeLatest } from 'redux-saga/effects';

import {
  expandQuoteItem,
  receiveQuoteItems,
  removeQuoteItems,
} from 'modules/quotes/quoteItems/data/actions';
import quoteItemService from 'modules/quotes/quoteItems/data/service';

import partService from 'services/partService';
import quoteService from 'services/quoteService';

import { receiveQuote } from 'store/quotes/actions';
import { getOrCreateQuote } from 'store/quotes/getOrCreateQuote';

import {
  bostonCenterlessUpdateFlow,
  bostonCenterlessUploadFlow,
  types,
} from './actions';
import { getInitialValues } from './reducer';

export default function* root() {
  yield takeLatest(types.BOSTON_CENTERLESS_UPLOAD_FLOW_SUBMIT, watchCreate);
  yield takeLatest(
    types.BOSTON_CENTERLESS_UPDATE_FLOW_REQUEST,
    watchUpdateRequest,
  );
  yield takeLatest(types.BOSTON_CENTERLESS_UPDATE_FLOW_SUBMIT, watchUpdate);
}

function* watchCreate({ payload }) {
  try {
    const {
      customAttrs,
      description,
      materialId,
      partName,
      partNumber,
      privateNotes,
      processId,
      publicNotes,
      quantities,
      supplierMaterialId,
    } = payload;

    const quote = yield call(getOrCreateQuote);

    const quoteId = quote.id;
    const quoteItems = yield call(
      quoteService.getQuoteItemHeadersById,
      quoteId,
    );

    const response = yield call(quoteItemService.createManualPart, quoteId, {
      customAttrs,
      description,
      finishOpDefIds: [],
      materialId,
      partName,
      partNumber,
      privateNotes,
      processId,
      publicNotes,
      quantities,
      supplierMaterialId,
    });

    // add new quote item and remove corresponding temporary quote item
    yield put(receiveQuote(quote));
    yield put(receiveQuoteItems(quoteItems.concat([response])));
    yield put(bostonCenterlessUploadFlow.success());
  } catch (error) {
    console.error(error);
    yield put(bostonCenterlessUploadFlow.error(error));
  }
}

function* watchUpdateRequest(action) {
  const { payload } = action;
  const { component, quoteItem } = payload;
  const {
    description,
    material: supplierMaterial,
    part,
    partNumber,
    process,
    quantities,
  } = component;
  const { filename } = part;
  const {
    id: quoteItemId,
    position: quoteItemPosition,
    privateNotes,
    publicNotes,
  } = quoteItem;

  try {
    yield put(
      bostonCenterlessUpdateFlow.openModal({
        customAttrs: [],
        description,
        materialId: ((supplierMaterial || {}).material || {}).id || null,
        partName: filename,
        partNumber,
        privateNotes,
        processId: (process || {}).id,
        publicNotes,
        quantities: quantities.map((q) => q.quantity),
        quoteItemId,
        quoteItemPosition,
        supplierMaterialId: (supplierMaterial || {}).id,
      }),
    );
  } catch (error) {
    console.error(error);
    yield put(bostonCenterlessUpdateFlow.openModal());
  }
}

function* watchUpdate(action) {
  const { payload } = action;
  const {
    customAttrs,
    description,
    materialId,
    isManual,
    partName,
    partNumber,
    privateNotes,
    processId,
    publicNotes,
    quantities,
    supplierMaterialId,
  } = payload;
  const initialValues = yield select(getInitialValues);
  const { quoteItemId, quoteItemPosition } = initialValues;

  try {
    const quote = yield call(getOrCreateQuote);
    const quoteId = quote.id;
    const quoteItems = yield call(
      quoteService.getQuoteItemHeadersById,
      quoteId,
    );

    // create new quote item
    const newQuoteItem = yield call(
      quoteItemService.createManualPart,
      quoteId,
      {
        customAttrs,
        description,
        finishOpDefIds: [],
        materialId,
        partName,
        partNumber,
        privateNotes,
        processId,
        publicNotes,
        quantities,
        supplierMaterialId,
      },
    );

    if (!isManual) {
      // merge the uploaded file into the new quote item
      // and remove the existing manual part file
      const mergedQuoteItem = yield call(
        quoteService.quoteItems.mergeItemsAsSupportingFiles,
        {
          quoteId,
          parentQuoteItemId: newQuoteItem.id,
          childrenQuoteItemIds: [quoteItemId],
        },
      );
      mergedQuoteItem.rootComponent.part.partFiles.forEach((partFile) => {
        if (!partFile.url) {
          partService.removeAttachedFile(partFile.uuid);
        }
      });
    } else {
      // delete existing quote item
      yield call(quoteService.quoteItems.bulkDelete, [quoteItemId]);
    }

    // patch quote item to update position to previous position
    const patchedQuoteItem = yield call(
      quoteService.quoteItems.update,
      newQuoteItem.id,
      { position: quoteItemPosition },
    );

    // receive new quote
    yield put(receiveQuote(quote));
    yield put(receiveQuoteItems([patchedQuoteItem]));
    yield put(bostonCenterlessUpdateFlow.success());
    yield put(
      expandQuoteItem.request({
        quoteItemId: newQuoteItem.id,
        replaceHistory: true,
      }),
    );
    yield put(
      removeQuoteItems(quoteItems.filter((qi) => qi.id == quoteItemId)),
    );
  } catch (error) {
    yield put(bostonCenterlessUpdateFlow.error(error));
  }
}
